/**
 *
 * TabCourses
 *
 */

import React, { memo, useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";


import history from "utils/history";

// styles
import "./style.scss";

// antd component
import { Form } from "antd";

// components
import CustomTable from "components/CustomTable";
import AddItemCard from "components/AddItemCard";

import SelectFieldAxios from "containers/SelectFieldAxios";

// utils
import {
  openNewRow,
  confirmSaveRowData,
  confirmRemoveRowData,
} from "components/Tabs/helper.js";
// utils
import { DAY_OF_WEEK_OPTIONS } from "utils/helpers/helper.constant";
import { getLevelLabel, getDayOfWeekObj } from "utils/helpers/helper.generateData";
import { getObjId } from "utils/helpers/helper.general";
import { getSortedArrayDayOfWeek } from "utils/helpers/helper.filter-sort";

function TabCourses({
  courses,
  updateList,
  classId,
  classTitle,
  classLevel,
  classYear,
  // rule props
  isTeacher,
  showActions,
  hideClass,
  canAddNew,
  // defult props
  className,
}) {
  const childClassNames = classNames("tab-courses-wrapper row-tabs", className);
  const { t, i18n } = useTranslation();

  const newKey = "new";

  const [currentCourses, setCurrentCourses] = useState(courses || []);
  const [tableRows, setTableRow] = useState([]);
  const [newElement, setNewElement] = useState({});
  const [showError, setShowError] = useState(false);

  // Form initialization for new row
  const [addNewFrom] = Form.useForm();

  // Set data for opened item
  useEffect(() => {
    setCurrentCourses(courses);
    const rows = getSortedArrayDayOfWeek(courses);
    setTableRow(rows);
  }, []);

  // Set data for opened item
  useEffect(() => {
    // set courses list
    if (JSON.stringify(currentCourses) !== JSON.stringify(courses)) {
      setCurrentCourses(courses);
      const rows = getSortedArrayDayOfWeek(courses);
      setTableRow(rows);
    }
  }, [courses, currentCourses]);

  // create new Row with form
  const makeNewRow = useMemo(() => {
    // initial data for SelectFieldAxios component
    const newElementSelect = {
      label: `${t("common.new")} ${t("labels.course")}`,
      name: "new_course",
      endponit: "courses/list",
      required: true,
      displayLabel: false,
      value: "",
      type: "axios-select",
      col: 24,
      onSelect: (LabeledValue, option) => {
        setNewElement(option?.item);
      },
      itemCallback: item => {
        return {
          key: item._id,
          label: `${item?.subject?.name} - ${getLevelLabel(item?.level)}`,
          value: item._id,
          item: item,
        };
      },
    };

    classId && (newElementSelect.endponitSend = { class_id: classId });

    return (
      <Form form={addNewFrom} name="add-new-element" autoComplete="off">
        <SelectFieldAxios
          className="table-select"
          endponit={newElementSelect.endponit}
          endponitSend={newElementSelect.endponitSend}
          itemCallback={newElementSelect.itemCallback}
          onSelect={newElementSelect.onSelect}
          item={newElementSelect}
        />
        {showError && (
          <span className="alert">{t("common.existInList")}</span>
        )}
      </Form>
    );
  }, [addNewFrom, classId, showError, setNewElement, i18n?.language]);

  // Remove and update current Element
  const removeElement = elementObj => {
    if (elementObj && elementObj._id) {
      const newElements =
        currentCourses.length > 0 &&
        currentCourses.filter(el => el._id !== elementObj._id);
      setCurrentCourses(newElements);
      updateList(newElements, "deleteCourse");
    }
  };

  // save/add new Element form new Row
  const addElement = () => {
    addNewFrom.submit();
    const val = addNewFrom.getFieldsValue();
    if (val.new_course && newElement) {
      const hasElement =
        currentCourses &&
        currentCourses.some(element => element._id === newElement._id);

      if (!hasElement) {
        setShowError(false);
        const newEl = {
          _id: newElement?._id,
          description: newElement?.description,
        };
        const newEls = [newEl, ...currentCourses];
        setCurrentCourses(newEls);
        updateList(newEls, "addCourse");
      } else {
        setShowError(true);
      }
    }
  };

  const actionsColumn = {
    title: t("common.action"),
    dataIndex: "action",
    key: "action-column-courses",
    className: "column-actions",
    render: (_, record) => {
      if (record.key === "initial") {
        return (
          <div className="row-actions">
            {confirmSaveRowData(tableRows, setTableRow, addElement)}
          </div>
        );
      } else {
        return confirmRemoveRowData(record, removeElement);
      }
    },
  };

  const columns = [
    {
      title: t("absences.day"),
      dataIndex: "course_week",
      key: "column-course_week",
      className: "column-course_week",
      sorter: (a, b) => a?.course_week.localeCompare(b?.course_week),
      renderHighlight: (text, record, children) => children,
      filters: DAY_OF_WEEK_OPTIONS()?.map(day => ({
        value: day?.value,
        text: day?.title || day?.label,
      })),
      onFilter: (value, record) => record.course_week == value,
      render: (text, record) => {
        if (record.key === "initial") {
          return {
            children: makeNewRow,
            props: {
              colSpan: showActions ? 3 : 2,
            },
          };
        } else {
          return {
            children: <>{getDayOfWeekObj(record?.course_week)}</>,
            props: {
              colSpan: 1,
            },
          };
        }
      },
    },
    {
      title: t("absences.timetable"),
      dataIndex: "course_time_range",
      key: "column-course_time_range",
      className: "column-course_time_range",
      sorter: (a, b) =>
        a?.course_time_range.localeCompare(b?.course_time_range),
      render: (text, record) => {
        if (record.key === "initial") {
          return {
            children: makeNewRow,
            props: {
              colSpan: showActions ? 3 : 2,
            },
          };
        } else {
          return {
            children: <>{record?.course_time_range}</>,
            props: {
              colSpan: 1,
            },
          };
        }
      },
    },
    {
      title: t("labels.subject"),
      dataIndex: "subject",
      key: "column-subject",
      className: "column-subject",
      sorter: (a, b) => a?.subject?.name.localeCompare(b?.subject?.name),
      render: (text, record) => {
        if (record.key === "initial") {
          return {
            children: makeNewRow,
            props: {
              colSpan: showActions ? 3 : 2,
            },
          };
        } else {
          return {
            children: (
              <Link
                className="link-table"
                to={`/сours/${record._id}`}
                style={{ color: record?.subject?.color }}
              >
                {record?.subject?.name}
              </Link>
            ),
            props: {
              colSpan: 1,
            },
          };
        }
      },
    },
    {
      title: t("labels.level"),
      dataIndex: "level",
      key: "column-level",
      className: "column-level",
      sorter: (a, b) =>
        a?.level.toString().localeCompare(b?.level.toString()),
      render: (text, record, index) => {
        if (record.key === "initial") {
          return {
            children: "",
            props: {
              colSpan: 0,
            },
          };
        } else {
          return {
            children: <div>{getLevelLabel(record?.level)}</div>,
            props: {
              colSpan: 1,
            },
          };
        }
      },
    },
    {
      title: t("courses.nameOf"),
      dataIndex: "course_code",
      key: "column-level",
      className: "column-level",
      sorter: (a, b) =>
        a?.course_code?.toString()?.localeCompare(b?.course_code?.toString()),
      render: (text, record, index) => {
        if (record.key === "initial") {
          return {
            children: "",
            props: {
              colSpan: 0,
            },
          };
        } else {
          return {
            children: <div>{record?.course_code}</div>,
            props: {
              colSpan: 1,
            },
          };
        }
      },
    },
  ];

  const courseRoomColumn = {
    title: t("classes.classRoom"),
    dataIndex: "class_room",
    key: "column-class_room",
    className: "column-class_room",
    render: (text, record, index) => {
      if (record.key === "initial") {
        return {
          children: "",
          props: {
            colSpan: 0,
          },
        };
      } else {
        return {
          children: <div>{record?.class_room}</div>,
          props: {
            colSpan: 1,
          },
        };
      }
    },
  };

  const responsobleColumn = {
    title: t("labels.responsible"),
    dataIndex: "responsible",
    key: "column-responsible",
    className: "column-responsible",
    sorter: (a, b) =>
      a?.responsible?.description?.lastname.localeCompare(
        b?.responsible?.description?.lastname,
      ),
    render: (text, record, index) => {
      const responsible = record?.responsible?.description
        ? `${record?.responsible?.description?.lastname} ${
            record?.responsible?.description?.firstname
          }`
        : "";
      if (record.key === "initial") {
        return {
          children: "",
          props: {
            colSpan: 0,
          },
        };
      } else {
        return {
          children: (
            <>
              {record?.responsible?._id ? (
                <>
                  {getObjId(isTeacher) != getObjId(record?.responsible) ? (
                    <Link
                      className="link-table"
                      to={`/utilisateurs/${record?.responsible?._id}`}
                    >
                      {responsible}
                    </Link>
                  ) : (
                    <>{responsible}</>
                  )}
                </>
              ) : (
                "--"
              )}
            </>
          ),
          props: {
            colSpan: 1,
          },
        };
      }
    },
  };

  const classColumn = {
    title: t("labels.classes"),
    dataIndex: "className",
    key: "column-class",
    className: "column-class",
    render: (text, record, index) => {
      const result = record?.classes?.map((classInfo, ix, arr) => {
        const sep = ix == 0 && arr?.length > 1 && ix != arr - 1 ? ", " : "";
        return (
          <>
            <Link className="link-table" to={`/classes/${classInfo?._id}`}>
              {classInfo?.title}
            </Link>
            {sep}
          </>
        );
      });
      if (record.key === "initial") {
        return {
          children: "",
          props: {
            colSpan: 0,
          },
        };
      } else {
        return {
          children: result,
          props: {
            colSpan: 1,
          },
        };
      }
    },
  };

  // show responsible column only if this table show not for teachers
  !isTeacher && columns.push(responsobleColumn);

  // hide class column for classe page
  !hideClass && columns.push(classColumn);

  // added course room column
  columns.push(courseRoomColumn);

  // Set action column object
  !!showActions && columns.push(actionsColumn);

  // Render
  return (
    <div className={childClassNames}>
      {canAddNew && (
        <div className="column full-width">
          <div className="row-tabs">
            <div className="column align-middle">
              <h4 className="tab-title">{t("labels.courses")}</h4>
            </div>

            <div className="column aside-column align-right">
              <AddItemCard
                title={t("courses.labelNameOf")}
                description={
                  currentCourses &&
                  `${currentCourses.length || 0} ${t(
                    "courses.addNewTabCardCourses",
                  )}`
                }
                addAction={() => {
                  // openNewRow(tableRows, setTableRow, addNewFrom);
                  history.push({
                    pathname: `/сours/${newKey}`,
                    state: {
                      level: classLevel || null,
                      classes: !!classId
                        ? [{ _id: classId, title: classTitle }]
                        : [],
                      year: classYear || null,
                    },
                  });
                }}
                className="course-add-card"
              />
            </div>
          </div>
        </div>
      )}
      <div className="column full-width">
        <CustomTable dataSource={tableRows || []} columns={columns} />
      </div>
    </div>
  );
}

TabCourses.defaultProps = {
  classLevel: 0,
  showActions: false,
  classTitle: "",
  hideClass: false,
  canAddNew: false,
  isTeacher: false,
};

TabCourses.propTypes = {
  courses: PropTypes.oneOfType([
    PropTypes.bool.isRequired,
    PropTypes.object.isRequired,
    PropTypes.array.isRequired,
  ]),
  classId: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.object,
    PropTypes.string,
  ]),
  isTeacher: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.object,
    PropTypes.string,
  ]),
  updateList: PropTypes.func.isRequired,
};

export default memo(TabCourses);
